import { formatCurrency } from '@if/core/utils'
import ErrorIcon from '@mui/icons-material/Error'
import { Alert, Card, Grid, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material'
import type { FieldArrayPathByValue } from '@src/services/react-hook-form'
import { memo, useState } from 'react'
import type { Control, FieldErrors, FieldValues } from 'react-hook-form'
import { useController, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AddButton from '../Common/Button/AddButton'
import DeleteIconButton from '../Common/Button/DeleteIconButton'
import EditIconButton from '../Common/Button/EditIconButton'
import ActionTableCell from '../Common/Table/ActionTableCell'
import FundedProductDialog from './FundedProductDialog'
import type { FundedProductSchemaType } from './loan-form-schema'

interface FundedProductRowProps {
  fundedProduct: FundedProductSchemaType
  errors?: FieldErrors<FundedProductSchemaType>
  onDelete: () => void
  onUpdate: (data: FundedProductSchemaType) => void
}

const FundedProductRow = memo(({ fundedProduct, errors, onUpdate, onDelete }: FundedProductRowProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <TableRow>
      <TableCell>
        {t(`enum.product.${fundedProduct.productId}`)}
        {errors?.productId?.message && (
          <Typography variant="inherit" color="error">
            {errors?.productId?.message}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {fundedProduct.amount && formatCurrency(fundedProduct.amount)}
        {errors?.amount?.message && (
          <Typography variant="inherit" color="error">
            {errors?.amount?.message}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {fundedProduct.description}
        {errors?.description?.message && (
          <Typography variant="inherit" color="error">
            {errors?.description?.message}
          </Typography>
        )}
      </TableCell>
      <ActionTableCell>
        <EditIconButton onClick={() => setOpen(true)} />
        <DeleteIconButton onClick={onDelete} />
      </ActionTableCell>
      <FundedProductDialog
        open={open}
        defaultValues={fundedProduct}
        onClose={() => setOpen(false)}
        onSubmit={onUpdate}
      />
    </TableRow>
  )
})

FundedProductRow.displayName = 'FundedProductRow'

interface Props<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldArrayPathByValue<TFieldValues, FundedProductSchemaType>
  values: FundedProductSchemaType[]
}

const FundedProductList = <TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  values,
}: Props<TFieldValues>) => {
  const { t } = useTranslation()
  const { remove, update, append } = useFieldArray({ name, control })
  const {
    fieldState: { error },
  } = useController({ name: name as never, control })
  const [open, setOpen] = useState(false)

  return (
    <Grid size={12}>
      <Card variant="outlined" sx={{ padding: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('loan.fundedProducts')}</Typography>
          <AddButton onClick={() => setOpen(true)} />
        </Stack>
        {error?.message && (
          <Alert icon={<ErrorIcon fontSize="inherit" />} severity="error">
            {error?.message}
          </Alert>
        )}
        <Table>
          <TableBody>
            {values.map((fundedProduct, index) => {
              return (
                <FundedProductRow
                  key={index}
                  errors={error ? (error as never)[index] : undefined}
                  fundedProduct={fundedProduct}
                  onDelete={() => remove(index)}
                  onUpdate={(d) => update(index, d as never)}
                />
              )
            })}
          </TableBody>
        </Table>
        <FundedProductDialog open={open} onClose={() => setOpen(false)} onSubmit={(d) => append(d as never)} />
      </Card>
    </Grid>
  )
}

export default FundedProductList
